import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ActionButton,
  Loading,
  ErrorState,
  EmptyState,
  Card,
} from "../../shared";
import { CreateCarModal } from "../AdminClientCars/CreateCarModal";
import { CarBrandLogo } from "../../shared/CarBrandLogo";
import { clientsApi, servicesApi } from "../../../services";
import { formatPhoneNumber } from "../../../utils/phoneUtils";
import type { Appointment, Car, Client, Service } from "../../../types";
import {
  resolvePriceForCarEffort,
  serviceHasEffortTierPrices,
} from "../../../utils/serviceEffortPrice";

export type ClientCarSelectMeta = {
  /** Resolved appointment price (tiers when defined, else base). */
  appointmentPrice: string;
};

interface ClientCarsModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: number;
  onCarSelect?: (car: Car, meta?: ClientCarSelectMeta) => void;
  selectedCarId?: number;
  loading?: boolean;
  /** Loads full service (effort prices); preferred over nested appointment service. */
  serviceId?: number | null;
  /** Fallback until `serviceId` fetch completes or if fetch fails. */
  serviceForPricing?: Appointment["service"] | null;
  formatServicePrice?: (resolvedAmount: string) => string;
}

export const ClientCarsModal: React.FC<ClientCarsModalProps> = ({
  isOpen,
  onClose,
  clientId,
  onCarSelect,
  selectedCarId,
  loading: externalLoading = false,
  serviceId,
  serviceForPricing,
  formatServicePrice,
}) => {
  const [client, setClient] = useState<Client | null>(null);
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateCarModal, setShowCreateCarModal] = useState(false);
  const [fetchedService, setFetchedService] = useState<Service | null>(null);
  const [serviceLoading, setServiceLoading] = useState(false);

  const fetchClientAndCars = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch client info
      const clientResponse = await clientsApi.getClientById(clientId);
      if (clientResponse.success && clientResponse.data) {
        setClient(clientResponse.data);
      } else {
        setError("Falha ao carregar informações do cliente");
        return;
      }

      // Fetch cars
      const carsResponse = await clientsApi.getCarsByClient(clientId);
      if (carsResponse.success && carsResponse.data) {
        setCars(carsResponse.data);
      } else {
        setError("Falha ao carregar veículos do cliente");
      }
    } catch (err) {
      setError("Erro ao carregar dados");
      console.error("Error fetching client and cars:", err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (isOpen && clientId) {
      fetchClientAndCars();
    }
  }, [isOpen, clientId, fetchClientAndCars]);

  useEffect(() => {
    if (!isOpen || !serviceId) {
      setFetchedService(null);
      setServiceLoading(false);
      return;
    }

    let cancelled = false;
    setServiceLoading(true);
    (async () => {
      try {
        const res = await servicesApi.getServiceById(serviceId);
        if (cancelled) return;
        if (res.success && res.data) {
          setFetchedService(res.data);
        } else {
          setFetchedService(null);
        }
      } catch {
        if (!cancelled) setFetchedService(null);
      } finally {
        if (!cancelled) setServiceLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, serviceId]);

  const effectiveService = fetchedService ?? serviceForPricing ?? null;
  const hasEffortTierPrices = serviceHasEffortTierPrices(effectiveService);

  const handleCarSelect = (car: Car) => {
    if (onCarSelect) {
      if (effectiveService) {
        onCarSelect(car, {
          appointmentPrice: resolvePriceForCarEffort(effectiveService, car.effort),
        });
      } else {
        onCarSelect(car);
      }
    }
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Veículos${client ? ` - ${client.name}` : ""}`}
        size="md"
      >
        <div className="space-y-6">
          {loading ? (
            <Loading message="Carregando veículos..." size="md" />
          ) : error ? (
            <ErrorState
              message={error}
              variant="card"
              onRetry={fetchClientAndCars}
            />
          ) : (
            <>
              {/* Client Info */}
              {client && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">👤</span>
                    <div>
                      <p className="font-medium text-blue-900">{client.name}</p>
                      <p className="text-sm text-blue-700">
                        {formatPhoneNumber(client.phoneNumber)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {serviceId != null && serviceLoading && !effectiveService && (
                <p className="text-sm text-gray-500">Carregando valores do serviço…</p>
              )}

              {effectiveService && !hasEffortTierPrices && (
                <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/60 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800/80">
                    Preço do serviço
                  </p>
                  <p className="mt-0.5 text-base font-bold tabular-nums text-emerald-800">
                    {formatServicePrice
                      ? formatServicePrice(
                          resolvePriceForCarEffort(effectiveService, undefined)
                        )
                      : `R$ ${resolvePriceForCarEffort(effectiveService, undefined)}`}
                  </p>
                  <p className="mt-1 text-xs text-emerald-900/70">
                    Mesmo valor para todos os esforços de veículo.
                  </p>
                </div>
              )}

              {/* Cars List */}
              {cars.length === 0 ? (
                <Card>
                  <EmptyState
                    icon=""
                    title="Nenhum veículo cadastrado"
                    description="Este cliente ainda não possui veículos cadastrados. Crie o primeiro veículo para começar."
                    actionText="Criar Primeiro Veículo"
                    onAction={() => setShowCreateCarModal(true)}
                  />
                </Card>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      Veículos ({cars.length})
                    </h3>
                    <ActionButton
                      onClick={() => setShowCreateCarModal(true)}
                      variant="primary"
                      size="sm"
                    >
                      + Novo Veículo
                    </ActionButton>
                  </div>

                  <div className="space-y-3">
                    {cars.map((car) => (
                      <div
                        key={car.id}
                        className={`p-4 bg-white dark:bg-slate-800 rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 transition-all ${
                          externalLoading
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                        } ${
                          selectedCarId === car.id
                            ? "border-blue-500 bg-blue-50"
                            : "hover:border-gray-300 hover:shadow-md"
                        }`}
                        onClick={() => !externalLoading && handleCarSelect(car)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <CarBrandLogo brand={car.brand} />
                            <div className="min-w-0">
                              <h4 className="text-lg font-semibold text-gray-900">
                                {car.model}
                              </h4>
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm text-gray-600">
                                  {car.plate}
                                </p>
                                {car.effort && (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                    car.effort === "Leve" 
                                      ? "bg-green-100 text-green-700" 
                                      : car.effort === "Médio"
                                      ? "bg-yellow-100 text-yellow-700"
                                      : "bg-red-100 text-red-700"
                                  }`}>
                                    {car.effort}
                                  </span>
                                )}
                              </div>
                              {car.effort && hasEffortTierPrices && effectiveService ? (
                                <p className="mt-2 text-sm font-semibold text-emerald-700 tabular-nums">
                                  Preço do serviço:{" "}
                                  {formatServicePrice
                                    ? formatServicePrice(
                                        resolvePriceForCarEffort(effectiveService, car.effort)
                                      )
                                    : `R$ ${resolvePriceForCarEffort(effectiveService, car.effort)}`}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          {selectedCarId === car.id && (
                            <div className="text-blue-600 flex-shrink-0">
                              <span className="text-xl">✓</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
          <ActionButton onClick={onClose} variant="outline" size="sm">
            Fechar
          </ActionButton>
        </div>
      </Modal>

      {/* Create Car Modal */}
      <CreateCarModal
        isOpen={showCreateCarModal}
        onClose={() => {
          setShowCreateCarModal(false);
          // Refresh the cars list after closing
          fetchClientAndCars();
        }}
        clientId={clientId}
        clientName={client?.name || ""}
      />
    </>
  );
};
